import {
  Container,
  Table,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Button,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import NavigationHeader from "../../../components/NavigationHeader";
import { useParams } from "react-router-dom";
import { Add as AddIcon, Link as LinkIcon } from "@mui/icons-material";
import InventoryActionModal from "./InventoryActionModal";
import {
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import {
  formatCurrency,
  formatDate,
  formatVehicleNumber,
} from "../../../utils/utils";
import AllocateItemModal from "./AllocateItemModal";
import { inventoryApi } from "./apiService";
import UnallocateItemModal from "./UnallocateItemModal";
import { Loading } from "../../../components/Loading";

export interface InventoryTableItem {
  id: string;
  partNumber: string;
  manufacturer: string;
  supplierName: string;
  amount: number;
  warrantyStartDate: string;
  warrantyPeriod: number;
  warrantyEndDate: string;
  warrantyNumber: string;
  status?: string;
  categoryId?: string;
  masterAllocation?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function InventoryItemTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const CategoryWiseTable = ({
  category,
  inventoryItems,
  handleAllocateItem,
}: {
  category: string;
  inventoryItems: InventoryTableItem[];
  handleAllocateItem: (item: InventoryTableItem) => void;
}) => {
  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small" aria-label="inventory table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              {category} Number
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Manufacturer
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Supplier
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Price
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Vehicle
            </TableCell>
            {/* <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
                Actions
              </TableCell> */}
          </TableRow>
        </TableHead>
        <TableBody>
          {inventoryItems.length > 0 ? (
            inventoryItems.map((item) => {
              return (
                <TableRow key={item?.id} hover>
                  <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                      {item?.partNumber}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                      {item?.manufacturer || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                      {item?.supplierName || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                      {formatCurrency(item?.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                      {item?.masterAllocation?.vehicle?.vehicleNumber ? (
                        `${formatVehicleNumber(
                          item?.masterAllocation?.vehicle?.vehicleNumber
                        )} (${formatDate(item?.masterAllocation?.startDate)})`
                      ) : (
                        <Button
                          variant="text"
                          onClick={() => handleAllocateItem(item)}
                          {...smallButtonProp}
                        >
                          Assign
                        </Button>
                      )}
                    </Typography>
                  </TableCell>
                  {/* <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                      <Button
                        variant="text"
                        onClick={() => handleAllocateItem(item)}
                        {...smallButtonProp}
                      >
                        {item?.masterAllocation?.vehicle?.vehicleNumber
                          ? category === "Tyre"
                            ? "Reassign"
                            : "Unassign"
                          : "Assign"}
                      </Button>
                    </TableCell> */}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={5}
                sx={{
                  ...tableCellStyles,
                  fontSize: "0.75rem",
                  textAlign: "center",
                }}
              >
                No {category} records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const VehicleWiseTable = ({
  category,
  inventoryItems,
  handleAllocateItem,
}: {
  category: string;
  inventoryItems: InventoryTableItem[];
  handleAllocateItem: (item: InventoryTableItem) => void;
}) => {
  // Filter out unallocated tyres and group by vehicle
  const allocatedItems = inventoryItems.filter(
    (item) => item?.masterAllocation?.vehicle?.vehicleNumber
  );

  const groupedByVehicle = allocatedItems.reduce((acc, item) => {
    const vehicleNumber = item?.masterAllocation.vehicle.vehicleNumber;
    if (!acc[vehicleNumber]) {
      acc[vehicleNumber] = [];
    }
    acc[vehicleNumber].push(item);
    return acc;
  }, {} as Record<string, InventoryTableItem[]>);

  const vehicleGroups = Object.entries(groupedByVehicle);

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small" aria-label="inventory table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              {category} Number
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Manufacturer
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Supplier
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Price
            </TableCell>
            <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              Assigned On
            </TableCell>
            {category === "Tyre" && (
              <TableCell sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
                Km's Reading
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {vehicleGroups.length > 0 ? (
            vehicleGroups.map(([vehicleNumber, items]) => (
              <React.Fragment key={vehicleNumber}>
                {/* Vehicle Header Row */}
                <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottomWidth: "0px",
                    },
                  }}
                >
                  <TableCell
                    colSpan={6}
                    sx={{
                      py: 1,
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {formatVehicleNumber(vehicleNumber)}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Tyre Rows for this vehicle */}
                {items.map((item) => (
                  <TableRow key={item?.id} hover>
                    <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {item?.partNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {item?.manufacturer || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {item?.supplierName || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {formatCurrency(item?.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyles, fontSize: "0.75rem" }}>
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {formatDate(item?.masterAllocation.startDate)}
                      </Typography>
                    </TableCell>
                    {category === "Tyre" && (
                      <TableCell
                        sx={{ ...tableCellStyles, fontSize: "0.75rem" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {item?.masterAllocation?.odometerReading || "N/A"}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={6}
                sx={{
                  ...tableCellStyles,
                  fontSize: "0.75rem",
                  textAlign: "center",
                }}
              >
                No allocated {category} records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const InventoryCategory = () => {
  const { category, categoryId } = useParams();
  const [addInventory, setAddInventory] = useState(false);
  const [editItem, setEditItem] = useState<InventoryTableItem | null>(null);
  const [viewItem, setViewItem] = useState<InventoryTableItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [allocateItemModal, setAllocateItemModal] = useState(false);
  const [unallocateItemModal, setUnallocateItemModal] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryTableItem[]>(
    []
  );
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const fetchInventoryItems = async () => {
    try {
      setLoading(true);
      const items = await inventoryApi.getInventoryItems(categoryId!);
      setInventoryItems(items);
    } catch (error) {
      console.error("Error fetching inventory items:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItems();
  }, [categoryId]);

  const handleModalClose = () => {
    setAddInventory(false);
    setEditItem(null);
    setViewItem(null);
  };

  const handleAllocateItemClose = () => {
    setAllocateItemModal(false);
    setViewItem(null);
  };

  const handleUnallocateItemClose = () => {
    setUnallocateItemModal(false);
    setViewItem(null);
  };

  const handleAllocateItem = (item: InventoryTableItem) => {
    if (item?.masterAllocation?.vehicle?.vehicleNumber) {
      setViewItem(item);
      setUnallocateItemModal(true);
    } else {
      setViewItem(item);
      setAllocateItemModal(true);
    }
  };

  const handleReallocateItem = () => {
    setUnallocateItemModal(false);
    setAllocateItemModal(true);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Inventory", path: "/master-data/inventory" },
          { label: category! },
        ]}
        title={category!}
        button={{
          label: `Add ${category}`,
          onClick: () => setAddInventory(true),
          icon: <AddIcon />,
        }}
      />

      {loading ? (
        <Loading message="Loading..." fullScreen={true} />
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab
                sx={{ fontSize: "0.75rem", px: 1 }}
                label={`${category} wise`}
                {...a11yProps(0)}
              />
              <Tab
                sx={{ fontSize: "0.75rem", px: 1 }}
                label={`Vehicle wise`}
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>
          <InventoryItemTabPanel value={value} index={0}>
            <CategoryWiseTable
              category={category!}
              inventoryItems={inventoryItems}
              handleAllocateItem={handleAllocateItem}
            />
          </InventoryItemTabPanel>
          <InventoryItemTabPanel value={value} index={1}>
            <VehicleWiseTable
              category={category!}
              inventoryItems={inventoryItems}
              handleAllocateItem={handleAllocateItem}
            />
          </InventoryItemTabPanel>
        </Box>
      )}

      <InventoryActionModal
        open={addInventory}
        onClose={handleModalClose}
        category={category!}
        categoryId={categoryId!}
        viewMode={!!viewItem}
        item={editItem || viewItem}
        isEdit={!!editItem}
        fetchInventoryItems={fetchInventoryItems}
      />

      <AllocateItemModal
        open={allocateItemModal}
        onClose={handleAllocateItemClose}
        item={viewItem}
        reallocate={Boolean(
          viewItem?.masterAllocation?.vehicle?.vehicleNumber &&
            category === "Tyre"
        )}
        fetchInventoryItems={fetchInventoryItems}
        category={category!}
      />

      <UnallocateItemModal
        open={unallocateItemModal}
        onClose={handleUnallocateItemClose}
        item={viewItem}
        category={category!}
        fetchInventoryItems={fetchInventoryItems}
        onReallocate={() => handleReallocateItem()}
        reallocate={Boolean(
          viewItem?.masterAllocation?.vehicle?.vehicleNumber &&
            category === "Tyre"
        )}
      />
    </Container>
  );
};

export default InventoryCategory;
